import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import DataTable from "../../../components/data-display/DataTable";
import {
  ApplicationButton,
  ApplicationPage,
  ApplicationSectionHeader,
  ApplicationStateRegion,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../../components/application";
import AssignmentActivityChooser from "../components/AssignmentActivityChooser";
import {
  AssignmentDatesSection,
  AssignmentDetailsSection,
  AssignmentPeopleSection,
} from "../components/AssignmentCompositionSections";
import {
  archiveAssignment,
  cancelAssignment,
  closeAssignment,
  getAssignmentMonitoring,
  getOwnedAssignment,
  inspectAssignmentActivity,
  listLaunchableEvaluationTasks,
  publishAssignment,
  updateDraftAssignment,
} from "../services/assignmentApi";
import {
  assignmentLifecycleLabel,
  assignmentLifecycleTone,
  formatAssignmentDate,
  monitoringStatusLabel,
  targetLabel,
} from "./assignmentViewUtils";
import "./assignmentPresentation.css";

const EMPTY_DRAFT_FORM = {
  title: "",
  description: "",
  instructions: "",
  evaluationTaskId: "",
  assigneeEmails: "",
  availableFrom: "",
  dueAt: "",
  closesAt: "",
};

const ASSIGNMENT_OPERATION_MESSAGES = {
  ASSIGNEE_NOT_FOUND:
    "Check the assignee email addresses. Each assignee must be an existing authenticated user.",
  ASSIGNEES_REQUIRED:
    "Add at least one assignee before publishing.",
  ASSIGNMENT_MUST_CLOSE_BEFORE_ARCHIVE:
    "Close or cancel this assignment before archiving it.",
  ASSIGNMENT_NOT_CANCELLABLE:
    "Closed or archived assignments cannot be cancelled.",
  ASSIGNMENT_NOT_CLOSABLE:
    "Only published assignments can be closed.",
  ASSIGNMENT_NOT_EDITABLE:
    "Only draft assignments can be edited. Refresh the page to see the current state.",
  ASSIGNMENT_NOT_FOUND:
    "This assignment could not be found.",
  ASSIGNMENT_NOT_PUBLISHABLE:
    "Only draft assignments can be published. Refresh the page to see the current state.",
  EVALUATION_TASK_NOT_FOUND:
    "Choose an available activity before saving.",
  EVALUATION_TASK_NOT_LAUNCHABLE:
    "Choose an available activity before saving.",
  EVALUATION_TASK_NOT_SELECTABLE_FOR_ASSIGNMENT:
    "Choose an available activity before saving.",
  EVALUATION_TASK_NOT_SUPPORTED_FOR_ASSIGNMENT:
    "Choose an activity supported for assignments.",
  FORBIDDEN:
    "You do not have permission to change this assignment.",
  INVALID_DATE:
    "Check the assignment dates and try again.",
  INVALID_DATE_ORDER:
    "Check the date order. Availability must be before the due and close dates, and the due date must be before the close date.",
  NOT_FOUND:
    "This assignment could not be found.",
  TITLE_REQUIRED:
    "Add a title before saving.",
};

const LIFECYCLE_GUIDANCE = {
  draft: {
    status: "information",
    message: "Draft assignment",
    detail:
      "You can keep editing this assignment. Participants cannot access it until you publish. You may also cancel or archive the draft; neither action deletes assignment records.",
  },
  published: {
    status: "information",
    message: "Published assignment",
    detail:
      "Assigned participants may start or continue according to the availability and close dates. Close stops start, continuation, and response submission; cancel ends the assignment. Existing records and participant progress are retained.",
  },
  closed: {
    status: "warning",
    message: "Closed assignment",
    detail:
      "Participants can no longer start, continue, or submit responses for this assignment. Existing records and participant progress are retained. You can archive it for organization; archive is not deletion.",
  },
  cancelled: {
    status: "destructive",
    message: "Cancelled assignment",
    detail:
      "This assignment has ended and participants can no longer start, continue, or submit responses. Existing records and participant progress are retained. You can archive it for organization; cancellation cannot be undone here.",
  },
  archived: {
    status: "information",
    message: "Archived assignment",
    detail:
      "This assignment is retained for organization and is not available for participant access. Assignment records and existing participant progress are retained. No lifecycle actions are available here.",
  },
};

function formatDateTimeLocalInput(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const localOffset = date.getTimezoneOffset() * 60000;

  return new Date(date.getTime() - localOffset)
    .toISOString()
    .slice(0, 16);
}

function normalizeDateTimeLocalPatch(value) {
  return value
    ? new Date(value).toISOString()
    : null;
}

function splitAssigneeEmails(value) {
  return String(value || "")
    .split(/[\n,]/)
    .map((email) => email.trim())
    .filter(Boolean);
}

function assignmentToDraftForm(assignment) {
  if (!assignment) {
    return EMPTY_DRAFT_FORM;
  }

  return {
    title: assignment.title || "",
    description: assignment.description || "",
    instructions: assignment.instructions || "",
    evaluationTaskId:
      assignment.target?.evaluationTaskId || "",
    assigneeEmails:
      (assignment.assignees || [])
        .map((assignee) => assignee.email)
        .filter(Boolean)
        .join("\n"),
    availableFrom:
      formatDateTimeLocalInput(assignment.availableFrom),
    dueAt:
      formatDateTimeLocalInput(assignment.dueAt),
    closesAt:
      formatDateTimeLocalInput(assignment.closesAt),
  };
}

function buildDraftPayload(form) {
  return {
    title: form.title,
    description: form.description,
    instructions: form.instructions,
    evaluationTaskId: form.evaluationTaskId,
    assigneeEmails:
      splitAssigneeEmails(form.assigneeEmails),
    availableFrom:
      normalizeDateTimeLocalPatch(form.availableFrom),
    dueAt:
      normalizeDateTimeLocalPatch(form.dueAt),
    closesAt:
      normalizeDateTimeLocalPatch(form.closesAt),
  };
}

function messageForAssignmentError(error, fallback) {
  const code =
    error?.response?.data?.error;

  return (
    ASSIGNMENT_OPERATION_MESSAGES[code] ||
    fallback
  );
}

function lifecycleGuidanceFor(status) {
  return (
    LIFECYCLE_GUIDANCE[status] ||
    {
      status: "information",
      message: "Assignment state",
      detail:
        "Refresh the page to see the latest available actions for this assignment.",
    }
  );
}

function closeConfirmationMessage() {
  return "Close this assignment? Participants will no longer be able to start, resume, continue, or submit responses. Existing assignment records and participant progress are retained. There is no reopen or undo action here.";
}

function cancelConfirmationMessage(status) {
  if (status === "draft") {
    return "Cancel this draft assignment? Unsaved changes currently visible in the form will not be saved. Participants will not be able to access it. Existing assignment records and participant progress are retained. This cannot be undone here.";
  }

  return "Cancel this assignment? Participants will no longer be able to start, resume, continue, or submit responses. Existing assignment records and participant progress are retained. This cannot be undone here.";
}

function archiveConfirmationMessage(status) {
  if (status === "draft") {
    return "Archive this draft assignment? Unsaved changes currently visible in the form will not be saved. Archiving files it for organization and does not delete assignment records or existing participant progress.";
  }

  return "Archive this assignment? Archiving files it for organization and does not delete assignment records or existing participant progress.";
}

function AssignmentStatusChip({ status }) {
  return (
    <span
      className={[
        "av-app-status-chip",
        `av-app-status-chip--${assignmentLifecycleTone(status)}`,
      ].join(" ")}
    >
      {assignmentLifecycleLabel(status)}
    </span>
  );
}

function LifecycleGuidance({ status }) {
  const guidance =
    lifecycleGuidanceFor(status);

  return (
    <ApplicationStateRegion
      status={guidance.status}
      message={guidance.message}
      detail={guidance.detail}
    />
  );
}

const MONITORING_COLUMNS = [
  { key: "participant", label: "Participant", width: "20%" },
  { key: "email", label: "Email", width: "22%" },
  { key: "status", label: "Status", width: "14%" },
  { key: "started", label: "Started", width: "15%" },
  { key: "completed", label: "Completed", width: "14%" },
  { key: "lastActivity", label: "Last activity", width: "15%" },
];

function monitoringRows(participants) {
  return participants.map(participant => ({
    id: participant.userId,
    participant: participant.displayName || participant.email || participant.userId,
    email: participant.email || "Not available",
    status: monitoringStatusLabel(participant.progress),
    started: formatAssignmentDate(participant.progress?.startedAt),
    completed: formatAssignmentDate(participant.progress?.completedAt),
    lastActivity: formatAssignmentDate(participant.progress?.lastActivityAt),
  }));
}

export default function FacilitatorAssignmentDetail() {
  const { assignmentId } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tasksError, setTasksError] = useState("");
  const [changingActivity, setChangingActivity] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const [inspectionError, setInspectionError] = useState("");
  const [draftForm, setDraftForm] = useState(EMPTY_DRAFT_FORM);
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadAssignment = useCallback(async () => {
    const [detail, monitoring, launchableResult] =
      await Promise.all([
        getOwnedAssignment(assignmentId),
        getAssignmentMonitoring(assignmentId).catch(() => null),
        listLaunchableEvaluationTasks().then(value => ({ value, error: false })).catch(() => ({ value: [], error: true })),
      ]);

    const loadedAssignment =
      detail?.assignment || null;

    setAssignment(loadedAssignment);
    setDraftForm(assignmentToDraftForm(loadedAssignment));
    setParticipants(monitoring?.participants || []);
    setTasks(launchableResult.value);
    setTasksError(launchableResult.error ? "Available Activities could not be loaded. The current Activity has been preserved; retry before changing it." : "");
  }, [assignmentId]);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        await loadAssignment();

        if (active) {
          setError("");
          setNotice("");
        }
      } catch (_loadError) {
        if (active) {
          setError(
            "This assignment could not be loaded, or you do not own it."
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [assignmentId, loadAssignment]);

  const draftTaskOptions = useMemo(() => {
    const optionsById =
      new Map(
        (tasks || [])
          .map((task) => ({
            ...task,
            _id: task._id || task.id,
          }))
          .filter((task) => task._id)
          .map((task) => [task._id, task])
      );

    const currentTargetId =
      assignment?.target?.evaluationTaskId;

    if (
      currentTargetId &&
      !optionsById.has(currentTargetId)
    ) {
      optionsById.set(currentTargetId, {
        ...assignment.target,
        _id: currentTargetId,
      });
    }

    return Array.from(optionsById.values());
  }, [assignment, tasks]);

  const selectedDraftActivity = useMemo(() => {
    const id = draftForm.evaluationTaskId;
    if (!id) return null;
    const task = draftTaskOptions.find(option => String(option._id || option.id) === String(id));
    if (!task) return null;
    return {
      ...task,
      id: task.id || task._id,
      name: task.name || targetLabel(task),
      provenance: task.provenance || (task.governance?.scope === "personal" ? "my_activity" : "provided_by_allvoiced"),
      version: task.version || task.lineage?.version,
      family: task.family || "perceptual_activity",
    };
  }, [draftForm.evaluationTaskId, draftTaskOptions]);

  async function viewActivity(activity) {
    try {
      setInspectionOpen(true);
      setInspectionLoading(true);
      setInspectionError("");
      setInspection(await inspectAssignmentActivity(activity.id, assignmentId));
    } catch (_inspectionError) {
      setInspection(null);
      setInspectionError("This activity could not be inspected. Please try again.");
    } finally {
      setInspectionLoading(false);
    }
  }

  async function retryActivities() {
    try {
      const loadedTasks = await listLaunchableEvaluationTasks();
      setTasks(loadedTasks);
      setTasksError("");
    } catch (_taskError) {
      setTasksError("Available Activities could not be loaded. The current Activity has been preserved; retry before changing it.");
    }
  }

  function updateDraftField(event) {
    const { name, value } = event.target;

    setDraftForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  async function persistDraft() {
    const updated =
      await updateDraftAssignment(
        assignmentId,
        buildDraftPayload(draftForm)
      );

    setAssignment(updated);
    setDraftForm(assignmentToDraftForm(updated));

    return updated;
  }

  async function handleSaveDraft(event) {
    event.preventDefault();

    try {
      setSavingDraft(true);
      await persistDraft();
      setError("");
      setNotice("Draft saved.");
    } catch (_saveError) {
      setError(
        messageForAssignmentError(
          _saveError,
          "Draft could not be saved. Confirm the activity, dates, and participant emails."
        )
      );
      setNotice("");
    } finally {
      setSavingDraft(false);
    }
  }

  async function reconcileLifecycleResult({
    updatedAssignment,
    successMessage,
    reloadFailureMessage,
  }) {
    setAssignment(updatedAssignment);
    setDraftForm(assignmentToDraftForm(updatedAssignment));
    setError("");
    setNotice(successMessage);

    try {
      await loadAssignment();
    } catch (_reloadError) {
      setError(reloadFailureMessage);
    }
  }

  async function handlePublish() {
    const confirmed =
      window.confirm(
        "Publish this assignment? Assigned participants will be able to access it according to the availability dates."
      );

    if (!confirmed) {
      return;
    }

    try {
      setPendingAction("publish");
      await persistDraft();
    } catch (_saveError) {
      setError(
        messageForAssignmentError(
          _saveError,
          "Draft could not be saved. Confirm the activity, dates, and participant emails."
        )
      );
      setNotice("");
      setPendingAction("");
      return;
    }

    try {
      const published =
        await publishAssignment(assignmentId);

      await reconcileLifecycleResult({
        updatedAssignment: published,
        successMessage: "Assignment published.",
        reloadFailureMessage:
          "Assignment was published, but the latest details could not be refreshed. Reload the page to confirm the current state.",
      });
    } catch (_publishError) {
      setError(
        messageForAssignmentError(
          _publishError,
          "Assignment could not be published."
        )
      );
      setNotice("");
    } finally {
      setPendingAction("");
    }
  }

  async function handleClose() {
    const confirmed =
      window.confirm(closeConfirmationMessage());

    if (!confirmed) {
      return;
    }

    try {
      setPendingAction("close");
      const closed =
        await closeAssignment(assignmentId);

      await reconcileLifecycleResult({
        updatedAssignment: closed,
        successMessage: "Assignment closed.",
        reloadFailureMessage:
          "Assignment was closed, but the latest details could not be refreshed. Reload the page to confirm the current state.",
      });
    } catch (_closeError) {
      setError(
        messageForAssignmentError(
          _closeError,
          "Assignment could not be closed."
        )
      );
      setNotice("");
    } finally {
      setPendingAction("");
    }
  }

  async function handleCancel() {
    const confirmed =
      window.confirm(
        cancelConfirmationMessage(
          assignment?.lifecycleStatus
        )
      );

    if (!confirmed) {
      return;
    }

    try {
      setPendingAction("cancel");
      const cancelled =
        await cancelAssignment(assignmentId);

      await reconcileLifecycleResult({
        updatedAssignment: cancelled,
        successMessage: "Assignment cancelled.",
        reloadFailureMessage:
          "Assignment was cancelled, but the latest details could not be refreshed. Reload the page to confirm the current state.",
      });
    } catch (_cancelError) {
      setError(
        messageForAssignmentError(
          _cancelError,
          "Assignment could not be cancelled."
        )
      );
      setNotice("");
    } finally {
      setPendingAction("");
    }
  }

  async function handleArchive() {
    const confirmed =
      window.confirm(
        archiveConfirmationMessage(
          assignment?.lifecycleStatus
        )
      );

    if (!confirmed) {
      return;
    }

    try {
      setPendingAction("archive");
      const archived =
        await archiveAssignment(assignmentId);

      await reconcileLifecycleResult({
        updatedAssignment: archived,
        successMessage: "Assignment archived.",
        reloadFailureMessage:
          "Assignment was archived, but the latest details could not be refreshed. Reload the page to confirm the current state.",
      });
    } catch (_archiveError) {
      setError(
        messageForAssignmentError(
          _archiveError,
          "Assignment could not be archived."
        )
      );
      setNotice("");
    } finally {
      setPendingAction("");
    }
  }

  if (loading) {
    return (
      <ApplicationPage variant="workspace">
        <ApplicationViewHeader
          contextLabel="Assignments"
          title="Loading assignment..."
        />
      </ApplicationPage>
    );
  }

  const canPublish =
    assignment?.lifecycleStatus === "draft";

  const isDraft =
    assignment?.lifecycleStatus === "draft";

  const canClose =
    assignment?.lifecycleStatus === "published";

  const canCancel =
    ["draft", "published"].includes(assignment?.lifecycleStatus);

  const canArchive =
    ["draft", "closed", "cancelled"].includes(
      assignment?.lifecycleStatus
    );

  const hasLifecycleActions =
    canClose || canCancel || canArchive;

  const actionPending =
    Boolean(pendingAction);

  return (
    <ApplicationPage variant="workspace">
      <ApplicationViewHeader
        contextLabel="Assignments"
        title={assignment?.title || "Assignment"}
        description={assignment?.description || ""}
      />

      <p>
        <Link to="/app/assignments">
          Back to assignments
        </Link>
      </p>

      {error && (
        <ApplicationStateRegion
          status="error"
          message={error}
        />
      )}

      {notice && (
        <ApplicationStateRegion
          status="success"
          message={notice}
        />
      )}

      {assignment && (
        <>
          {isDraft ? (
            <ApplicationSurface>
              <h2>Edit draft assignment</h2>
              <AssignmentStatusChip
                status={assignment.lifecycleStatus}
              />
              <LifecycleGuidance
                status={assignment.lifecycleStatus}
              />

              <form onSubmit={handleSaveDraft}>
                <ApplicationSurface id="assignment-activity" variant="subtle">
                  <h3>Activity</h3>
                  {tasksError ? <><ApplicationStateRegion status="error" message={tasksError} /><ApplicationButton type="button" variant="secondary" onClick={retryActivities}>Retry Activities</ApplicationButton></> : null}
                  {!tasksError && assignment?.target?.evaluationTaskId && !(tasks || []).some(task => String(task.id || task._id) === String(assignment.target.evaluationTaskId)) ? <ApplicationStateRegion status="warning" message="This Activity is retained in the draft but is no longer available for publication or new targeting. Choose another Activity before publishing." /> : null}
                  <AssignmentActivityChooser
                    activities={draftTaskOptions.map(task => ({ ...task, id: task.id || task._id, name: task.name || targetLabel(task), provenance: task.provenance || (task.governance?.scope === "personal" ? "my_activity" : "provided_by_allvoiced"), family: task.family || "perceptual_activity" }))}
                    selectedActivity={selectedDraftActivity}
                    changing={changingActivity}
                    inspection={inspection}
                    inspectionLoading={inspectionLoading}
                    inspectionError={inspectionError}
                    inspectionOpen={inspectionOpen}
                    assignmentId={assignmentId}
                    onChoose={(activity) => { setDraftForm(current => ({ ...current, evaluationTaskId: activity.id })); setChangingActivity(false); }}
                    onChange={() => setChangingActivity(true)}
                    onCancelChange={() => setChangingActivity(false)}
                    onView={viewActivity}
                    onCloseInspection={() => { setInspectionOpen(false); setInspection(null); setInspectionError(""); }}
                  />
                </ApplicationSurface>
                <AssignmentPeopleSection form={draftForm} onChange={updateDraftField} />
                <AssignmentDatesSection form={draftForm} onChange={updateDraftField} />
                <AssignmentDetailsSection form={draftForm} onChange={updateDraftField} />

                <div className="av-app-form-actions">
                  <ApplicationButton
                    disabled={savingDraft || actionPending}
                    type="submit"
                  >
                    {savingDraft ? "Saving..." : "Save draft"}
                  </ApplicationButton>

                  {canPublish && (
                    <ApplicationButton
                      disabled={savingDraft || actionPending}
                      onClick={handlePublish}
                      type="button"
                      variant="secondary"
                    >
                      {pendingAction === "publish"
                        ? "Publishing..."
                        : "Publish assignment"}
                    </ApplicationButton>
                  )}

                  {canCancel && (
                    <ApplicationButton
                      disabled={savingDraft || actionPending}
                      onClick={handleCancel}
                      type="button"
                      variant="destructive"
                    >
                      {pendingAction === "cancel"
                        ? "Cancelling..."
                        : "Cancel assignment"}
                    </ApplicationButton>
                  )}

                  {canArchive && (
                    <ApplicationButton
                      disabled={savingDraft || actionPending}
                      onClick={handleArchive}
                      type="button"
                      variant="secondary"
                    >
                      {pendingAction === "archive"
                        ? "Archiving..."
                        : "Archive assignment"}
                    </ApplicationButton>
                  )}
                </div>
              </form>
            </ApplicationSurface>
          ) : (
            <ApplicationSurface className="av-assignment-detail">
              <ApplicationSectionHeader
                title={targetLabel(assignment.target)}
                contextLabel="Activity"
              />
              <dl className="av-assignment-metadata">
                <dt>Activity</dt>
                <dd>{targetLabel(assignment.target)}</dd>
                <dt>Status</dt>
                <dd>
                  <AssignmentStatusChip
                    status={assignment.lifecycleStatus}
                  />
                </dd>
                <dt>Assignees</dt>
                <dd>{assignment.assigneeCount}</dd>
                <dt>Available from</dt>
                <dd>{formatAssignmentDate(assignment.availableFrom)}</dd>
                <dt>Due</dt>
                <dd>{formatAssignmentDate(assignment.dueAt)}</dd>
                <dt>Closes</dt>
                <dd>{formatAssignmentDate(assignment.closesAt)}</dd>
              </dl>

              {assignment.instructions && (
                <div className="av-assignment-detail__instructions">
                  <h3>Assignment instructions</h3>
                  <p>{assignment.instructions}</p>
                </div>
              )}

              <LifecycleGuidance
                status={assignment.lifecycleStatus}
              />

              {hasLifecycleActions && (
                <>
                  <h3>Lifecycle actions</h3>
                  <p>
                    Close stops participant access for a published assignment.
                    Cancel ends a draft or published assignment. Archive files
                    an inactive assignment for organization. Lifecycle actions
                    retain assignment records and existing participant progress.
                  </p>

                  <div className="av-app-form-actions">
                    {canClose && (
                      <ApplicationButton
                        disabled={actionPending}
                        onClick={handleClose}
                        variant="secondary"
                      >
                        {pendingAction === "close"
                          ? "Closing..."
                          : "Close assignment"}
                      </ApplicationButton>
                    )}

                    {canCancel && (
                      <ApplicationButton
                        disabled={actionPending}
                        onClick={handleCancel}
                        variant="destructive"
                      >
                        {pendingAction === "cancel"
                          ? "Cancelling..."
                          : "Cancel assignment"}
                      </ApplicationButton>
                    )}

                    {canArchive && (
                      <ApplicationButton
                        disabled={actionPending}
                        onClick={handleArchive}
                        variant="secondary"
                      >
                        {pendingAction === "archive"
                          ? "Archiving..."
                          : "Archive assignment"}
                      </ApplicationButton>
                    )}
                  </div>
                </>
              )}
            </ApplicationSurface>
          )}

          {!isDraft && <ApplicationSurface>
            <h2 id="participant-monitoring-heading">Participant monitoring</h2>

            {participants.length === 0 ? (
              <ApplicationStateRegion
                status="empty"
                message="No participant monitoring yet."
              />
            ) : (
              <DataTable
                ariaLabelledby="participant-monitoring-heading"
                className="av-assignment-monitoring-table"
                columns={MONITORING_COLUMNS}
                minWidth="62rem"
                rows={monitoringRows(participants)}
              />
            )}
          </ApplicationSurface>}
        </>
      )}
    </ApplicationPage>
  );
}
